<template>
  <div>
    <v-dialog
      v-if="showFullScreenView"
      v-model="showFullScreenView"
      fullscreen
      hide-overlay
      dark
    >
      <v-card
        flat
      >
        <v-card-title>
          {{ fleet.displayName() }} Multi-View
          <v-spacer />
          <v-btn icon small @click="showFullScreenView = false" class="mr-0 pl-1"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <v-row no-gutters>
            <v-card
              v-for="input in sortedPreviews"
              v-bind:key="input.device.device_guid + input.id"
              class="ma-1"
              flat
              width="480px"
            >
              <v-card-title>
                <device-label :device="input.device" />
              </v-card-title>
              <v-card-text>
                <span class="caption">{{ inputName(input) }}</span>
                <input-preview :device_guid="input.device.device_guid" :input_id="input.id" :input_type="input.type" :tile="true" :hide_silent="hideSilentAudioChannels" />
              </v-card-text>
            </v-card>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
    
    <v-toolbar
      flat
      dense
      :color="($vuetify.theme.dark) ? '' : 'grey lighten-5'"
      align="center"
    >
      <div class="text-subtitle-2 mr-3">Sort By</div>
      
      <v-btn-toggle
        class="ma-1"
        :value="multiviewSortBy"
        mandatory
        divided
      >
        <v-btn x-small value="device_name" @click="setSortBy('device_name')">Device Name</v-btn>
        <v-btn x-small value="input_name" @click="setSortBy('input_name')">Input Name</v-btn>
      </v-btn-toggle>
      
      <v-btn v-if="multiviewSortOrder == 'desc'" x-small text @click="setSortOrder('asc')"><v-icon small>mdi-arrow-down</v-icon></v-btn>
      <v-btn v-if="multiviewSortOrder == 'asc'" x-small text @click="setSortOrder('desc')"><v-icon small>mdi-arrow-up</v-icon></v-btn>
      
      <v-switch
        class="ma-1 ml-3"
        inset
        dense
        label="Hide Silent Audio Channels"
        :inputValue="hideSilentAudioChannels"
        @change="setHideSilentAudioChannels(!hideSilentAudioChannels)"
        hide-details
      ></v-switch>
      
      <v-spacer />
      
      <v-btn
        v-if="devicePreviews.length > 0"
        class="mr-1"
        small
        depressed
        @click="showFullScreenView = true"
      >
        <v-icon>fullscreen</v-icon> Full Screen
      </v-btn>
    </v-toolbar>
    
    <v-row no-gutters v-if="!showFullScreenView" class="px-3 pb-3">
      <div v-if="!fleet.ready" class="mx-5 my-10">
        <v-progress-linear
          color="primary"
          indeterminate
          buffer-value="0"
        ></v-progress-linear>
      </div>
      
      <v-col v-if="fleet.ready && devicePreviews.length == 0" cols="12" class="my-5 text-center">
        <span class="text--disabled">No previews available</span>
      </v-col>
      
      <v-card
        v-for="input in sortedPreviews"
        v-bind:key="input.device.device_guid + input.id"
        class="ma-1"
        flat
        width="480px"
      >
        <v-card-title>
          <device-label :device="input.device" />
        </v-card-title>
        <v-card-text>
          <span class="caption">{{ inputName(input) }}</span>
          <input-preview :device_guid="input.device.device_guid" :input_id="input.id" :input_type="input.type" :tile="true" :hide_silent="hideSilentAudioChannels" />
        </v-card-text>
      </v-card>
    </v-row>
  </div>
</template>
<script>
  import { mapGetters } from 'vuex'

  import InputPreview from '../../device/components/InputPreview.vue'
  
  export default {
    name: 'FleetMultiview',
    
    props: ['fleet_guid', 'fleet'],
    
    components: {
      InputPreview
    },
    
    data() {
      return {
        inputPreviewSupportedVersion: this.$helpers.parseVersion('cloud', 'v1.3.0'),
        
        showFullScreenView: false,
      }
    },
    computed: {
      ...mapGetters('user', ['user', 'guid', 'isOrgAdmin']),
      ...mapGetters('userPreferences', ['multiviewSortBy', 'multiviewSortOrder', 'hideSilentAudioChannels']),
      
      devicePreviews() {
        var previews = []
        
        for (var device of this.fleet.devices) {
          if (device.isOnline() && device.hasFeature('services.cloud.fleet_multiview.enabled')) {
            var inputsShadow = this.$deviceShadows.getDeviceShadow(device.device_guid, 'Inputs')
            if (inputsShadow.reported) {
              var inputs = inputsShadow.editableStateCopy()
              
              for (var index in inputs) {
                var input = inputs[index]
                
                if (this.isPreviewSupported(device, index) && input.video_input_format != 'FORMAT_UNDEFINED') {
                  input.device = device
                  previews.push(input)
                }
              }
            }
          }
        }
        
        return previews
      },
      
      sortedPreviews() {
        var previews = this.devicePreviews
        
        return previews.sort((a, b) => {
          if (this.multiviewSortBy == 'device_name') {
            var deviceNameA = a.device.deviceName().toLowerCase()
            var deviceNameB = b.device.deviceName().toLowerCase()
            
            if (deviceNameA < deviceNameB) {
              return (this.multiviewSortOrder == 'asc') ? -1 : 1
            }
            
            if (deviceNameA > deviceNameB) {
              return (this.multiviewSortOrder == 'asc') ? 1 : -1
            }
          } else if (this.multiviewSortBy == 'input_name') {
            var inputNameA = this.inputName(a).toLowerCase()
            var inputNameB = this.inputName(b).toLowerCase()
            
            if (inputNameA < inputNameB) {
              return (this.multiviewSortOrder == 'asc') ? -1 : 1
            }
            
            if (inputNameA > inputNameB) {
              return (this.multiviewSortOrder == 'asc') ? 1 : -1
            }
          }
          
          return 0
        })
      }
    },
    
    mounted() {
      
    },
    
    methods: {
      isPreviewSupported(device, index = 0) {
        if (device.thumbnailEnabled(index) && this.$helpers.versionCheck(this.inputPreviewSupportedVersion, device.cloud_version)) {
          return true
        }
        return false
      },
      
      inputName(item) {
        if (item.config && item.config.name) {
          return this.$helpers.htmlDecode(item.config.name)
        }
        
        return 'Input #' + item.id
      },
      
      setSortBy(sortBy) {
        this.$store.dispatch('userPreferences/setMultiviewSortBy', sortBy)
      },
      
      setSortOrder(order) {
        this.$store.dispatch('userPreferences/setMultiviewSortOrder', order)
      },
      
      setHideSilentAudioChannels(hide) {
        this.$store.dispatch('userPreferences/setHideSilentAudioChannels', hide)
      }
    }
  }
</script>